import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Popup, Table } from '_atoms';
import { Context } from '_templates/AnalyticsDashboard/analytics-dashboard';
import { candidateMap } from '_templates/utils';
import { analyticWidgets, type AnalyticsFilterBody } from 'actions/analyticActions';
import { ReactComponent as Upload } from 'dist/icons/upload.svg';
import Date from 'dist/images/common/icons/graph.svg';
import { AnalyticsEmpty } from '../AnalyticsDashboard/AnalyticsEmpty/analytics-empty';
import {
  COLUMNS,
  COLUMNS_BE,
  DEFAULT_SHOWN_COLUMNS,
  OPTIONS,
  type AnalyticsCandidateRow,
  type AnalyticsCandidateTableOptions,
  type AnalyticsCandidatesWidgetResponse,
} from './constants';
import './style.scss';

const getInitialShownColumns = (): Record<string, boolean> => {
  const stored = sessionStorage.getItem('analyticsCandidates');
  return stored ? JSON.parse(stored) : DEFAULT_SHOWN_COLUMNS;
};

export const AnalyticsCandidateInfo = () => {
  const { t } = useTranslation();
  const { appliedFilters } = useContext(Context)!;
  const [candidates, setCandidates] = useState<AnalyticsCandidateRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<AnalyticsCandidateTableOptions>(OPTIONS);
  const [shownColumns, setShownColumns] = useState<Record<string, boolean>>(getInitialShownColumns);
  const [exportPopupOpen, setExportPopupOpen] = useState(false);
  const [successPopupOpen, setSuccessPopupOpen] = useState(false);

  const updateCandidatesTableParams = useCallback(
    (params: Partial<AnalyticsCandidateTableOptions>) => setOptions(p => ({ ...p, ...params })),
    []
  );

  const exportExcel = () => {
    analyticWidgets(
      'candidates/export/xlsx',
      {
        ...appliedFilters,
        ...options,
        offset: (options.page - 1) * options.limit.value,
        limit: options.limit.value,
        sorting: [options.sorting],
        columns: Object.entries(shownColumns)
          .filter(([, value]) => !!value)
          .map(([key]) => COLUMNS_BE[key]),
      } as AnalyticsFilterBody,
      null
    ).then(() => {
      setSuccessPopupOpen(true);
      setExportPopupOpen(false);
    });
  };

  const getCandidates = (opts: AnalyticsCandidateTableOptions & AnalyticsFilterBody) => {
    setLoading(true);
    analyticWidgets(
      'candidates',
      { ...opts, sorting: [opts.sorting] } as AnalyticsFilterBody,
      null,
      `?limit=${opts.limit.value}&offset=${(opts.page - 1) * opts.limit.value}`
    )
      .then((resp: AnalyticsCandidatesWidgetResponse) => {
        updateCandidatesTableParams({ itemsCount: resp.count, next: !!resp.next, prev: !!resp.previous });
        setCandidates(candidateMap(resp) as AnalyticsCandidateRow[]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getCandidates({
      ...options,
      ...appliedFilters,
    });
  }, [options.page, options.limit, options.sorting, appliedFilters]);

  if (!candidates.length && !loading) {
    return (
      <AnalyticsEmpty
        text={t('translation:analytics_candidate_info.text_no_results')}
        title={t('translation:analytics_candidate_info.title_no_results')}
        className="shadow"
        img={Date}
      />
    );
  }

  return (
    <>
      <Table
        rowKey="uuid"
        className="analytics-candidates"
        cardTitle={t('translation:analytics_candidate_info.card_title')}
        cardCount={options.itemsCount}
        extra={
          <Button onClick={() => setExportPopupOpen(true)} icon={<Upload />} className="export-excel">
            {t('translation:analytics_candidate_info.button_export_excel')}
          </Button>
        }
        columns={COLUMNS(options.sorting, sorting => updateCandidatesTableParams({ sorting }))}
        data={candidates}
        limit={options.limit}
        page={options.page}
        updateParams={(key, value) =>
          updateCandidatesTableParams({ [key]: value } as Partial<AnalyticsCandidateTableOptions>)
        }
        itemsCount={options.itemsCount}
        loading={loading}
        customizable
        tableName="analyticsCandidates"
        shownColumns={shownColumns}
        setShownColumns={setShownColumns}
      />
      <Popup
        open={exportPopupOpen}
        title={t('translation:analytics_candidate_info.popup_title_export_excel')}
        cancelButtonProps={{
          onClick: () => setExportPopupOpen(false),
          children: t('translation:analytics_candidate_info.button_go_back'),
        }}
        okButtonProps={{
          onClick: exportExcel,
          children: t('translation:analytics_candidate_info.button_send_to_email'),
        }}
      >
        {t('translation:analytics_candidate_info.text_export_excel')}
      </Popup>
      <Popup
        open={successPopupOpen}
        title={t('translation:analytics_candidate_info.popup_title_success')}
        okButtonProps={{
          onClick: () => setSuccessPopupOpen(false),
          children: t('translation:analytics_candidate_info.button_ok'),
        }}
      >
        {t('translation:analytics_candidate_info.text_file_sent')}
      </Popup>
    </>
  );
};
